/*
 * NEXZ V2 — PDP interaction layer (Stage E)
 *
 * One native lifecycle only: Horizon's own variant-picker/product-form
 * machinery (the `change` event on its radio inputs, and the
 * `shopify:product:select` event it dispatches via @shopify/events).
 * This module does not reimplement variant resolution, availability
 * checking, or cart submission — product-form.js / add-to-cart-component
 * already own that. It only adds what doesn't exist natively:
 *
 *   1. Gates the primary ATC button behind an explicit, conscious size
 *      choice. Horizon always resolves to selected_or_first_available_
 *      variant on load, so *something* is technically pre-checked before
 *      the customer acts — that is not the same as the customer having
 *      chosen it (doc 09's fit-certainty psychology). The gate is a
 *      capture-phase click intercept (no aria-disabled), not the native
 *      `disabled` attribute, because product-form.js actively manages
 *      that attribute around its own async add-to-cart flow and would
 *      silently defeat a gate built on the same property.
 *   2. Reveals the per-size measurement line matching the checked size
 *      (all size rows are server-rendered, unset-aware; this only
 *      toggles which one is visible).
 *   3. Opens the notify-me dialog when an out-of-stock size is chosen
 *      (Horizon's native picker leaves OOS options genuinely selectable,
 *      not `disabled` — it relies on the add-to-cart flow to show
 *      "Unavailable"; this module intercepts that specific case instead
 *      to redirect it into the capture flow doc 09 asks for).
 *   4. Fires a documented, undestined event-hook interface on `document`
 *      (see NEXZ_PDP_EVENT_CONTRACT below) — no analytics destination is
 *      wired in Stage E; a later stage attaches a real listener.
 *
 * Single product per page, so this operates at document scope rather
 * than a scoped root — Horizon's block system renders the variant
 * picker, buy-buttons, and this file's sibling snippets as independent
 * sibling blocks (not nested in a common NEXZ wrapper), so scoping to
 * one element wouldn't reach all of them anyway.
 *
 * Deep-link behavior: a `?variant=...` query string means the customer
 * arrived with explicit size intent (a shared or bookmarked link). The
 * server renders that size checked and the gate starts open. Without it
 * no size is checked (snippets/nexz-require-choice.liquid).
 */
(function () {
  var GATE_ATTR = 'data-nexz-size-required';

  /**
   * Documented event-hook contract. Every emit uses this dispatcher so
   * the payload shape stays consistent and inspectable from the console
   * (`document.addEventListener('nexz:add_to_cart', console.log)`)
   * without this file itself writing to the console or a dataLayer.
   */
  var NEXZ_PDP_EVENT_CONTRACT = [
    'view_item',
    'size_guide_open',
    'size_selected',
    'fit_stack_view',
    'oos_notify_submit',
    'add_to_cart',
    'gallery_swipe',
    'video_play',
    // Stage F additions (doc 23):
    'set_module_view',
    'set_add',
  ];

  function nexzEmit(name, detail) {
    if (NEXZ_PDP_EVENT_CONTRACT.indexOf(name) === -1) return;
    document.dispatchEvent(new CustomEvent('nexz:' + name, { detail: detail || {}, bubbles: true }));
  }

  function currentChecked(picker) {
    return picker.querySelector('input[type="radio"]:checked');
  }

  function initBuyColumn() {
    var picker = document.querySelector('variant-picker[data-product-id]');
    // The main form's button. The sticky bar renders a second
    // [ref="addToCartButton"] earlier in the DOM; it doesn't submit by
    // itself but "puppet"-clicks this one, so gating this one covers both.
    var atcButton = document.querySelector('form[data-type="add-to-cart-form"] [ref="addToCartButton"]');
    var fitStack = document.querySelector('.nexz-fit-stack');
    var notifyDialog = document.querySelector('.nexz-notify-dialog');
    var notifyBodyField = notifyDialog ? notifyDialog.querySelector('[data-nexz-notify-body]') : null;
    var productId = picker ? picker.dataset.productId : undefined;
    var productTitle = picker ? picker.dataset.productTitle : undefined;

    if (fitStack) nexzEmit('fit_stack_view', { productId: productId });
    if (!picker || !atcButton) return;

    var selectSizeTextEl = document.querySelector('[data-nexz-select-size-text]');
    var selectSizeText = selectSizeTextEl ? selectSizeTextEl.textContent : '';
    var addedTextEl = document.querySelector('[data-nexz-added-text]');
    var addedText = addedTextEl ? addedTextEl.textContent : '';
    var atcTextContent = atcButton.querySelector('.add-to-cart-text__content');
    var addedTimer = null;

    // Doc 13: brief "ADDED ✓" confirmation on a real add. CSS-driven
    // (see nexz-pdp.css) — this only toggles the attribute + label so it
    // never fights product-form.js's own text management. Idempotent:
    // a second add before the first clears just resets the timer.
    function flashAdded() {
      if (!atcTextContent || !addedText) return;
      atcTextContent.setAttribute('data-nexz-added-label', addedText);
      atcButton.setAttribute('data-nexz-added', 'true');
      if (addedTimer) clearTimeout(addedTimer);
      addedTimer = setTimeout(function () {
        atcButton.removeAttribute('data-nexz-added');
      }, 1600);
    }

    function setGate(active) {
      // No aria-disabled: Horizon greys out disabled buttons, but this one
      // still answers a press (with the size prompt), so it stays an
      // ordinary button whose label says what to do.
      if (active) {
        atcButton.setAttribute(GATE_ATTR, 'true');
        if (selectSizeText && atcTextContent) {
          atcTextContent.setAttribute('data-nexz-select-size-label', selectSizeText);
        }
      } else {
        atcButton.removeAttribute(GATE_ATTR);
      }
    }

    function syncMeasurement(sizeValue) {
      if (!fitStack || !sizeValue) return;
      var rows = fitStack.querySelectorAll('[data-nexz-size-spec]');
      for (var i = 0; i < rows.length; i++) {
        rows[i].hidden = rows[i].getAttribute('data-nexz-size-spec') !== sizeValue;
      }
      fitStack.setAttribute('data-nexz-selected-size', sizeValue);
    }

    function openNotifyDialog(sizeValue, radio) {
      if (!notifyDialog) return;
      if (notifyBodyField) {
        notifyBodyField.value =
          'Product: ' +
          (productTitle || '') +
          '\nSize: ' +
          sizeValue +
          '\nVariant ID: ' +
          (radio.dataset.variantId || 'not resolvable — combination unavailable');
      }
      notifyDialog.showModal();
    }

    // Capture phase: fires before product-form.js's own submit handling,
    // so the gate holds regardless of that component's internal
    // disabled-attribute management. This only blocks the click when
    // gated — it does not emit add_to_cart itself. product-form.js
    // dispatches CartLinesUpdateEvent optimistically, before the network
    // request resolves, so "the button was clicked" and "the cart line
    // was actually added" are different moments; see the document-level
    // listener below for the one that fires the analytics event.
    // Inline prompt shown when someone presses the button before picking
    // a size. One live region, announced politely; the size group gets
    // aria-invalid and focus moves to its first available size.
    var fieldset = picker.querySelector('fieldset');
    var prompt = document.createElement('p');
    prompt.className = 'nexz-size-prompt';
    prompt.id = 'nexz-size-prompt-' + (productId || 'pdp');
    prompt.setAttribute('role', 'status');
    prompt.setAttribute('aria-live', 'polite');
    prompt.hidden = true;
    var selectSizeErrorEl = document.querySelector('[data-nexz-select-size-error]');
    if (fieldset) fieldset.after(prompt);

    function showPrompt() {
      if (!fieldset) return;
      prompt.textContent = selectSizeErrorEl ? selectSizeErrorEl.textContent : selectSizeText;
      prompt.hidden = false;
      fieldset.setAttribute('aria-invalid', 'true');
      fieldset.setAttribute('aria-describedby', prompt.id);
      var first = fieldset.querySelector('input[type="radio"]:not([data-option-available="false"])');
      // The sticky bar may have triggered this from the bottom of the
      // page; bring the sizes into view before moving focus to them.
      fieldset.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      if (first) first.focus({ preventScroll: true });
    }

    function clearPrompt() {
      prompt.hidden = true;
      if (fieldset) {
        fieldset.removeAttribute('aria-invalid');
        fieldset.removeAttribute('aria-describedby');
      }
    }

    atcButton.addEventListener(
      'click',
      function (event) {
        if (atcButton.getAttribute(GATE_ATTR) === 'true') {
          event.preventDefault();
          event.stopImmediatePropagation();
          showPrompt();
        }
      },
      { capture: true }
    );

    // The sticky bar's button animates "added to cart" before it forwards
    // the press, so it is stopped too while no size is chosen: the shopper
    // is taken to the sizes instead of seeing a false confirmation.
    // Horizon dispatches on:click from a capturing listener on document,
    // so this listens on window (capture), which runs before it.
    window.addEventListener(
      'click',
      function (event) {
        var sticky = event.target.closest && event.target.closest('sticky-add-to-cart [ref="addToCartButton"]');
        if (!sticky || atcButton.getAttribute(GATE_ATTR) !== 'true') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        showPrompt();
      },
      { capture: true }
    );

    // Sold-out sizes are announced as unavailable (aria-disabled) and never
    // become the selection: a press opens the register-interest dialog
    // instead, and the radio stays unchecked so nothing can be added.
    picker.addEventListener(
      'click',
      function (event) {
        var radio = event.target.closest && event.target.closest('input[type="radio"][data-option-available="false"]');
        if (!radio) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        openNotifyDialog(radio.value, radio);
      },
      { capture: true }
    );

    picker.addEventListener('change', function () {
      var radio = currentChecked(picker);
      if (!radio) return;
      var value = radio.value;

      if (radio.dataset.optionAvailable === 'false') {
        setGate(true);
        openNotifyDialog(value, radio);
        return;
      }

      setGate(false);
      clearPrompt();
      syncMeasurement(value);
      nexzEmit('size_selected', { productId: productId, size: value });
    });

    // Horizon's own post-fetch confirmation (async) — used only as a
    // fallback sync point, not the primary gate trigger, so the gate
    // release is instant rather than waiting on a network round trip.
    document.addEventListener('shopify:product:select', function () {
      var radio = currentChecked(picker);
      if (radio && radio.dataset.optionAvailable !== 'false') syncMeasurement(radio.value);
    });

    // add_to_cart fires here, not from the button click above.
    // product-form.js dispatches CartLinesUpdateEvent synchronously when
    // the submit *starts* (action: 'add', context: 'product'), before
    // the cart/add.js fetch has resolved — the event carries a deferred
    // `.promise` that resolves once the request completes, with
    // `detail.didError` set if the server rejected it (also mirrored by
    // a separate CartErrorEvent, which this module does not listen to —
    // the resolved-but-errored promise branch below already covers it).
    // Awaiting that promise, rather than the click or the dispatch
    // itself, is what makes this represent a real cart addition and not
    // just an attempt: a network failure rejects the promise, a
    // server-side rejection resolves it with didError: true, and either
    // way nexz:add_to_cart does not fire. One listener, one dispatch
    // point in Horizon's code per submit, so this cannot double-fire.
    //
    // source: 'primary' vs 'sticky' — sticky-add-to-cart.js's
    // handleAddToCartClick doesn't submit its own form; it sets
    // atcButton.dataset.puppet = 'true' then calls atcButton.click(),
    // so this is the exact same submit, same event. The puppet flag is
    // read synchronously here (before the .then() below), not inside
    // it: sticky-add-to-cart.js resets the flag inside its own
    // .finally() on the same promise, and .then()/.finally() callbacks
    // on one promise run in attachment order, which this module cannot
    // rely on being before or after its own. Reading dataset.puppet at
    // dispatch time (synchronous, before either promise callback has
    // run) avoids that race entirely.
    document.addEventListener('shopify:cart:lines-update', function (event) {
      if (event.context === 'set') {
        handleSetAdd(event);
        return;
      }
      if (event.action !== 'add' || event.context !== 'product' || !event.promise) return;
      var line = event.lines && event.lines[0];
      if (!line) return;
      var source = atcButton.dataset.puppet === 'true' ? 'sticky' : 'primary';
      event.promise.then(function (result) {
        if (!result || (result.detail && result.detail.didError) || (result.userErrors && result.userErrors.length)) {
          return;
        }
        var merchandiseId = String(line.merchandiseId);
        var variantId = merchandiseId.slice(merchandiseId.lastIndexOf('/') + 1);
        var radio = picker.querySelector('input[data-variant-id="' + variantId + '"]');
        flashAdded();
        nexzEmit('add_to_cart', {
          productId: productId,
          size: radio ? radio.value : currentChecked(picker)?.value,
          variantId: variantId,
          quantity: line.quantity,
          source: source,
        });
      }, function () {
        // Promise rejected (network/unexpected error) — no confirmed
        // addition, so no event.
      });
    });

    // Complete-the-Set (Stage F): nexz-complete-set.js dispatches its own
    // CartLinesUpdateEvent with context: 'set' and two lines (this
    // product's variant + the companion's) — handled separately from the
    // primary/sticky path above rather than shoehorned into it, since
    // there's no single button/gate driving it and no per-size context.
    function handleSetAdd(event) {
      if (!event.promise) return;
      event.promise.then(function (result) {
        if (!result || (result.detail && result.detail.didError)) return;
        nexzEmit('add_to_cart', { productId: productId, source: 'set', lines: event.lines });
      }, function () {
        // Rejected — no confirmed addition, no event.
      });
    }

    // The server leaves every size unchecked unless the URL names a
    // variant (snippets/nexz-require-choice.liquid), so "is a size
    // checked?" is the whole test.
    var initial = currentChecked(picker);
    if (initial && initial.dataset.optionAvailable !== 'false') {
      setGate(false);
      syncMeasurement(initial.value);
    } else {
      setGate(true);
    }
  }

  function initSizeGuide() {
    var trigger = document.querySelector('[data-nexz-size-guide-trigger]');
    var dialog = document.querySelector('dialog.nexz-size-guide');
    if (!trigger || !dialog) return;
    trigger.addEventListener('click', function () {
      dialog.showModal();
      nexzEmit('size_guide_open', {});
    });
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });
  }

  function initNotifyDialog() {
    var dialog = document.querySelector('.nexz-notify-dialog');
    if (!dialog) return;
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });
    var form = dialog.querySelector('.nexz-notify-form');
    if (form) {
      form.addEventListener('submit', function () {
        var picker = document.querySelector('variant-picker[data-product-id]');
        var radio = picker ? currentChecked(picker) : null;
        nexzEmit('oos_notify_submit', {
          productId: picker ? picker.dataset.productId : undefined,
          size: radio ? radio.value : undefined,
        });
      });
    }
  }

  // Stage F: fires set_module_view once, the first time the Complete the
  // Set module scrolls into view — an impression signal, not "exists in
  // the DOM" (a module below the fold on load hasn't been seen yet).
  function initCompleteSet() {
    var setModule = document.querySelector('.nexz-complete-set');
    if (!setModule || typeof IntersectionObserver === 'undefined') return;
    var seen = false;
    var observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting && !seen) {
          seen = true;
          nexzEmit('set_module_view', { productId: setModule.dataset.partnerProductId });
          observer.disconnect();
        }
      }
    });
    observer.observe(setModule);

    setModule.addEventListener('nexz:set_add', function (event) {
      nexzEmit('set_add', event.detail);
    });
  }

  // Stage F: all-variants-unavailable state (doc 09 block 15). Opens the
  // same per-product notify dialog the per-size OOS path uses, but with
  // no size in the message — this is a different function from
  // openNotifyDialog() in initBuyColumn() specifically because that one
  // always writes a "Size: X" line, which doesn't apply here.
  function initSoldOut() {
    var trigger = document.querySelector('[data-nexz-sold-out-notify-trigger]');
    var notifyDialog = document.querySelector('.nexz-notify-dialog');
    if (!trigger || !notifyDialog) return;
    var notifyBodyField = notifyDialog.querySelector('[data-nexz-notify-body]');
    var soldOutBlock = document.querySelector('[data-nexz-sold-out]');
    trigger.addEventListener('click', function () {
      if (notifyBodyField) {
        notifyBodyField.value =
          'Product: ' + (soldOutBlock ? soldOutBlock.dataset.productId : '') + '\nAll sizes sold out.';
      }
      notifyDialog.showModal();
    });
  }

  function initGallery() {
    var scroller = document.querySelector('[data-nexz-gallery-scroller]');
    if (scroller) {
      var lastEmit = 0;
      scroller.addEventListener(
        'scrollend',
        function () {
          var now = Date.now();
          if (now - lastEmit < 300) return;
          lastEmit = now;
          nexzEmit('gallery_swipe', {});
        },
        { passive: true }
      );
    }
    document.querySelectorAll('main video').forEach(function (video) {
      video.addEventListener('play', function () {
        nexzEmit('video_play', {});
      });
    });
  }

  if (document.querySelector('variant-picker[data-product-id]')) {
    nexzEmit('view_item', {});
    initBuyColumn();
    initSizeGuide();
    initNotifyDialog();
    initGallery();
  }
  initCompleteSet();
  initSoldOut();
})();
